import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Car, Timer } from 'lucide-react'
import { getContentList } from '../../lib/api/content'
import { useToggleEnabled } from '../conventions/useToggleEnabled'
import { PageHead, Stat, Switch } from './ui'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'

const LIST_KEY = ['content', 'duration', 'all'] as const

function durationLabel(hours: unknown): string {
  const h = Number(hours)
  if (!Number.isFinite(h) || h <= 0) return '—'
  if (h < 24) return `${h} ${h === 1 ? 'ora' : 'ore'}`
  if (h % 168 === 0) {
    const w = h / 168
    return `${w} ${w === 1 ? 'settimana' : 'settimane'}`
  }
  if (h % 24 === 0) {
    const d = h / 24
    return `${d} ${d === 1 ? 'giorno' : 'giorni'}`
  }
  return `${h} ore`
}

const SEG = [
  { value: 'all', label: 'Tutte' },
  { value: 'active', label: 'Attive' },
  { value: 'inactive', label: 'Disattive' },
] as const

type Row = { id: string; name: string; transportId: string; hours: number; enabled: boolean }

export function DurationsPage() {
  const [seg, setSeg] = useState<(typeof SEG)[number]['value']>('all')
  const [busyId, setBusyId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => getContentList('duration', { limit: 300 }),
  })
  const transports = useQuery({
    queryKey: ['opt', 'transport'],
    queryFn: () => getContentList('transport', { limit: 50 }),
    staleTime: Infinity,
  })
  const toggle = useToggleEnabled('duration', LIST_KEY)

  const transportLabel = (id: string) => {
    const r = transports.data?.data?.find((t) => String(t.id) === String(id))
    const e = (r?.elements ?? {}) as El
    return r ? String((e.name as string) ?? (e.label as string) ?? `Veicolo ${id}`) : id ? `Veicolo ${id}` : '—'
  }

  const rows = useMemo<Row[]>(() => {
    return (list.data?.data ?? []).map((r) => {
      const el = r.elements as El
      const ref = el.transportId
      const transportId =
        ref && typeof ref === 'object' ? String((ref as { id?: unknown }).id ?? '') : String(ref ?? '')
      return {
        id: r.id,
        name: String(el.name ?? el.label ?? 'Senza nome'),
        transportId,
        hours: Number(el.hours) || 0,
        enabled: isYes(el.enabled),
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, transports.data])

  const sorted = useMemo(() => {
    return [...rows].sort(
      (a, b) =>
        Number(a.transportId) - Number(b.transportId) || a.hours - b.hours || a.name.localeCompare(b.name, 'it'),
    )
  }, [rows])

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.enabled).length
    const vehicles = new Set(rows.map((r) => r.transportId)).size
    return { total: rows.length, active, vehicles }
  }, [rows])

  const filtered = sorted.filter((r) => (seg === 'active' ? r.enabled : seg === 'inactive' ? !r.enabled : true))

  function flip(r: Row) {
    setBusyId(r.id)
    toggle.mutate({ id: r.id, enabled: !r.enabled }, { onSettled: () => setBusyId(null) })
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Opzioni durata sosta"
        description="Le fasce di durata che il cliente può scegliere per ogni tipo di veicolo. Attiva o disattiva una fascia direttamente da qui."
        model="duration"
      />

      <div className="mb-[18px] grid grid-cols-3 gap-[14px]">
        <Stat label="Fasce di durata" value={String(stats.total)} sub="opzioni configurate" />
        <Stat label="Attive" value={String(stats.active)} sub="selezionabili" />
        <Stat label="Veicoli coperti" value={String(stats.vehicles)} sub="con almeno una fascia" />
      </div>

      <div className="mb-[18px] inline-flex overflow-hidden rounded-lg border border-line bg-card">
        {SEG.map((s) => (
          <button
            key={s.value}
            onClick={() => setSeg(s.value)}
            className={cn(
              'px-3.5 py-2 text-[13px] font-semibold transition-colors',
              seg === s.value ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {list.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare le opzioni di durata.
        </div>
      ) : (
        <div className="cv-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-line-soft text-[11px] font-bold uppercase tracking-[1px] text-muted">
                  <th className="px-5 py-3 text-left">Fascia</th>
                  <th className="px-4 py-3 text-left">Tipo di veicolo</th>
                  <th className="px-4 py-3 text-right">Durata</th>
                  <th className="px-4 py-3 text-right">Attiva</th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-line-soft last:border-0">
                      {Array.from({ length: 4 }).map((_c, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="skeleton h-3.5 w-24 rounded-sm bg-line-soft" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-16 text-center text-sm text-muted">
                      {rows.length === 0 ? 'Nessuna opzione di durata configurata.' : 'Nessuna fascia nel filtro.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr
                      key={r.id}
                      className={cn('border-b border-line-soft last:border-0 hover:bg-paper', !r.enabled && 'opacity-60')}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper text-slate ring-1 ring-line">
                            <Timer className="h-4 w-4" />
                          </span>
                          <span className="font-semibold text-coal">{r.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-slate">
                          <Car className="h-3.5 w-3.5 text-faint" /> {transportLabel(r.transportId)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold tnum text-coal">
                        {durationLabel(r.hours)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <Switch on={r.enabled} busy={busyId === r.id} onClick={() => flip(r)} label={`Attiva ${r.name}`} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
